/* db-footer-align.js - 16 Sep 2026
   Keeps the footer social icons ending on the same line as LOG IN / SIGN UP.

   WHY THIS IS NOT CSS. The icons and the buttons sit in two different grid
   items of #t4s-footer .t4s-footer-wrap. Their heights depend on where the
   copy wraps AND on when the lazy media above settles, and the account
   column's content does not always reach its own box bottom - the gap was 0
   on some loads and 20px on others with identical markup. Every fixed offset
   tried against that was right on one render and a full line out on the next.

   THE REFERENCE IS THE WORD, NOT THE BUTTON (16 Sep, revised). This first
   aligned the glyph's bottom to the BUTTON's bottom. That is geometrically
   tidy and visually wrong: the button is 37.5px tall and its label is a
   13px/18.2px line sitting in the middle of it, so matching the box bottom
   put the 19px glyph 9.9px lower than the word - which reads on screen as the
   icon being centred ON the SIGN UP line rather than sitting on it. The eye
   aligns an icon to the TEXT beside it, not to the padding box around that
   text. So the reference is now the label's own line box, measured with a
   Range over the button's text node, and both sets of bottoms land together.

   HOW. The distance between the icon GLYPH's bottom edge (not the 44px tap
   target around it) and the label's bottom edge is applied as a transform,
   which moves the row without changing any layout height - so nothing it
   touches can feed back into the measurement.

   WHY IT RE-RUNS SO OFTEN. The first version aligned on load, fonts, resize
   and a short tail of timers, and still came out 20px low on some views: the
   footer settles late on long pages, after the last timer has fired. Watching
   #t4s-footer's own size is not enough either - the two columns can swap
   which one is taller without the footer's outer box changing at all. So this
   watches the TWO ELEMENTS THAT ACTUALLY DECIDE the offset, re-checks when
   the footer first scrolls into view, and watches the footer's markup for
   late-rendered blocks.

   Desktop only, matching the CSS: below 768px the footer is a different
   layout and the transform is cleared. */
(function () {
  var pending = false;

  /* the button's LABEL line box. Falls back to the button's own box if the
     label is wrapped in an element rather than sitting as a bare text node,
     or if the Range measures to nothing - a wrong-by-9px alignment beats no
     alignment at all. */
  function labelRect(btn) {
    var node = null;
    var walk = document.createTreeWalker(btn, NodeFilter.SHOW_TEXT, null);
    var n;
    while ((n = walk.nextNode())) {
      if (n.textContent && n.textContent.trim()) { node = n; break; }
    }
    if (node) {
      try {
        var rg = document.createRange();
        rg.selectNodeContents(node);
        var r = rg.getBoundingClientRect();
        if (r && r.height > 0) return r;
      } catch (e) {}
    }
    return btn.getBoundingClientRect();
  }

  function measure() {
    var ul = document.querySelector('#t4s-footer .t4s-socials-block .t4s-list-socials');
    if (!ul) return;

    /* mobile is a different layout - this is the ONLY case that clears */
    if (window.innerWidth < 768) { ul.style.transform = ''; return; }

    /* NEVER CLEAR WHEN A REFERENCE IS MISSING. An earlier version bailed out
       via the same branch as mobile, so any run that happened before the
       account buttons had rendered wiped a correct transform and left the
       icons 20px low with no further run to fix it. That is exactly the fault
       this file exists to prevent. If either reference is absent, leave the
       last good value alone and try again. */
    var btn = document.querySelector('#t4s-footer .shop-buttons-wrapper a');
    var glyph = ul.querySelector('a svg') || ul.querySelector('a');
    if (!btn || !glyph) { setTimeout(align, 250); return; }

    ul.style.transform = '';
    var d = glyph.getBoundingClientRect().bottom - labelRect(btn).bottom;
    if (Math.abs(d) > 0.5) ul.style.transform = 'translateY(' + (-d) + 'px)';
  }

  /* Coalesce bursts of calls into one measurement.
     NOT requestAnimationFrame ALONE. rAF does not fire in a hidden tab, so a
     page opened in a background tab - middle-click, "open in new tab", a
     session restore - would run every trigger below and measure nothing, then
     sit misaligned until something else happened to fire. Found because the
     automation tab this was tested in is itself hidden: the script ran, was
     served correctly, parsed correctly, and did nothing at all. When the page
     is hidden this falls back to a timer, and a visibilitychange listener
     re-checks the moment the tab is shown. */
  function align() {
    if (pending) return;
    pending = true;
    var run = function () { pending = false; measure(); };
    if (typeof requestAnimationFrame === 'function' && !document.hidden) {
      requestAnimationFrame(run);
    } else {
      setTimeout(run, 16);
    }
  }

  function watch() {
    var footer = document.querySelector('#t4s-footer');
    if (!footer) return;

    /* the two elements whose heights decide the offset. Watching the footer
       as a whole misses the case where they swap which is taller without the
       footer's outer box changing size. */
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(align);
      ro.observe(footer);
      [
        '#t4s-footer .t4s-socials-block',
        '#t4s-footer .shop-buttons-wrapper'
      ].forEach(function (sel) {
        var el = document.querySelector(sel);
        if (el) ro.observe(el);
      });
    }

    /* on a long page the footer settles only when it is reached */
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { align(); setTimeout(align, 300); obs.unobserve(e.target); }
        });
      }, { rootMargin: '200px' }).observe(footer);
    }

    /* the account buttons can render late; re-align when the footer's markup
       changes rather than assuming it is final */
    if (window.MutationObserver) {
      new MutationObserver(align).observe(footer, { childList: true, subtree: true });
    }

    /* any image finishing inside the footer can move a column */
    footer.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', align, { once: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { align(); watch(); });
  } else {
    align();
    watch();
  }
  window.addEventListener('load', function () { align(); watch(); });
  window.addEventListener('resize', align);
  document.addEventListener('visibilitychange', align);
  window.addEventListener('orientationchange', align);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(align);
  [100, 400, 1000, 2500, 5000].forEach(function (t) { setTimeout(align, t); });
})();
